/*

  View.ts

  Single page UI for Netskrafl/Explo using the Mithril library

  Copyright (C) 2025 Miðeind ehf.
  Author: Vilhjálmur Þorsteinsson

  The Creative Commons Attribution-NonCommercial 4.0
  International Public License (CC-BY-NC 4.0) applies to this software.
  For further information, see https://github.com/mideind/Netskrafl

  This UI is built on top of Mithril (https://mithril.js.org), a lightweight,
  straightforward JavaScript single-page reactive UI library.

  The page is structured into models, actions and views,
  cf. https://github.com/pakx/the-mithril-diaries/wiki/Basic-App-Structure

*/

import { t } from "./i18n";
import { IView, DialogViewEnum, ZOOM_FACTOR } from "./types";
import {
  scrollMovelistToBottom, coord, toVector,
} from "./util";
import {
  m, Vnode, VnodeChildren,
} from "./mithril";
import { Model } from "./model";
import { Actions } from "./actions";
import { WaitDialog, AcceptDialog } from "./wait";
import { FriendPromoteDialog } from "./friend";
import {
  Spinner,
  UserId,
  LeftLogo
} from "./components";
import { ChallengeDialog } from "./challengedialog";
import { Main } from "./main";
import { makeTabs, selectTab, TabVnode } from "./tabs";
import { UserInfoDialog } from "./userinfodialog";
import { UserPrefsDialog } from "./userprefsdialog";
import { GamePromptDialogs } from "./gamepromptdialogs";
import { GameView } from "./gameview";
import { Review } from "./review";

type DialogFunc = (view: View, args: any) => VnodeChildren;

type DialogViews = Record<DialogViewEnum, DialogFunc>;

interface Dialog {
  name: DialogViewEnum;
  args: any;
}

export class View implements IView {

  // The View class exposes the vwApp view function.
  // Each instance maintains a current dialog window stack.

  // The actions that the view can call
  actions: Actions;

  // The model that the view is attached to (accessed through actions)
  get model(): Model {
    return this.actions.model;
  }

  // The currently displayed dialogs
  private dialogStack: Dialog[] = [];

  // Map of available dialogs
  private static dialogViews: DialogViews = {
    userprefs:
      (view) => view.vwUserPrefs(),
    userinfo:
      (view, args) => m(UserInfoDialog, { key: "userinfodialog-" + args.userid, view, userid: args.userid, nick: args.nick, fullname: args.fullname }),
    challenge:
      (view, args) => m(ChallengeDialog, { key: "challenge", view, item: args }),
    friend:
      (view) => m(FriendPromoteDialog, { key: "friend", view }),
    wait:
      (view, args) => view.vwWait(args),
    accept:
      (view, args) => view.vwAccept(args)
  };

  // The current scaling of the board
  boardScale: number = 1.0;

  // Pending zoom animation timeout (for cancellation across rapid zoom operations)
  private zoomAnimationTimeout: ReturnType<typeof setTimeout> | null = null;

  selectedTab: string = "movelist";

  constructor(actions: Actions) {

    this.actions = actions;

    // Initialize media listeners now that we have the view reference
    this.actions.initMediaListener(this);

    // Set up callback to attach user listener when Firebase is ready
    // This handles both fresh login and cached authentication cases
    const state = this.model.state;
    if (state) {
      state.onFirebaseReady = () => {
        // Firebase Database is now initialized, attach user listener
        this.actions.attachListenerToUser();
      };
    }

    // Load user preferences early so audio settings are available
    // Use false to not show spinner on initial load
    this.model.loadUser(false);
  }

  appView(routeName: string): VnodeChildren {
    // Returns a view based on the current route.
    // Displays the appropriate content for the route,
    // also considering active dialogs.
    const model = this.model;
    let views: VnodeChildren = [];
    switch (routeName) {
      case "main":
        views.push(m(Main, { key: "main", view: this }));
        break;
      case "game":
        views.push(m(GameView, { key: "game", view: this }));
        break;
      case "review":
        views.push(m(Review, { key: "review", view: this }));
        break;
      case "help":
        // A route parameter of ?q=N goes directly to the FAQ number N
        // A route parameter of ?tab=N goes directly to tab N (0-based)
        views.push(
          this.vwHelp(
            parseInt(m.route.param("tab") || ""),
            parseInt(m.route.param("faq") || "")
          )
        );
        break;
      default:
        return [ m("div", { key: "error" }, t("Þessi vefslóð er ekki rétt")) ];
    }
    // Push any open dialogs
    for (const dialog of this.dialogStack) {
      const v = View.dialogViews[dialog.name];
      if (v === undefined) {
        console.error("Unknown dialog name: " + dialog.name);
      } else {
        const n = v(this, dialog.args);
        n && views.push(n);
      }
    }
    // Overlay a spinner, if active
    if (model.spinners)
      views.push(m(Spinner, { key: "spinner" }));
    return views;
  }

  // Dialog support

  pushDialog(dialogName: DialogViewEnum, dialogArgs?: any) {
    this.dialogStack.push({ name: dialogName, args: dialogArgs });
    m.redraw(); // Ensure that the dialog is shown
  }

  popDialog() {
    if (this.dialogStack.length > 0) {
      this.dialogStack.pop();
      m.redraw();
    }
  }

  popAllDialogs() {
    if (this.dialogStack.length > 0) {
      this.dialogStack = [];
      m.redraw();
    }
  }

  isDialogShown() {
    return this.dialogStack.length > 0;
  }

  startSpinner() {
    this.actions.showSpinner();
  }

  stopSpinner() {
    this.actions.hideSpinner();
  }

  notifyMediaChange() {
    // The view is changing, between mobile and fullscreen
    // and/or between portrait and landscape: ensure that
    // we don't end up with a selected game tab that is not visible
    const model = this.model;
    if (model.state?.uiFullscreen || model.state?.uiLandscape) {
      // In this case, there is no board tab:
      // show the movelist
      if (this.setSelectedTab("movelist"))
        setTimeout(scrollMovelistToBottom);
    }
    else {
      // Mobile: we default to the board tab
      this.setSelectedTab("board");
    }
    // When switching between landscape and portrait,
    // close all current dialogs
    this.popAllDialogs();
  }

  notifyChatMessage() {
    // A fresh chat message has arrived
    // and has been added to the chat message list
    m.redraw();
  }

  zoomIn() {
    this.boardScale = ZOOM_FACTOR;
  }

  zoomOut() {
    if (this.boardScale !== 1.0) {
      this.boardScale = 1.0;
      setTimeout(() => this.resetScale());
    }
  }

  resetScale() {
    // Reset the board scale (zoom) to 100% and the scroll origin to (0, 0)
    // Cancel any pending zoom animation
    if (this.zoomAnimationTimeout !== null) {
      clearTimeout(this.zoomAnimationTimeout);
      this.zoomAnimationTimeout = null;
    }
    this.boardScale = 1.0;
    const boardParent = document.getElementById("board-parent");
    const board = boardParent?.children[0] as HTMLElement;
    if (board) {
      board.style.transition = 'none';
      board.style.transform = `translate(0px, 0px) scale(1)`;
    }
    if (boardParent)
      boardParent.scrollTo(0, 0);
  }

  updateScale() {

    const model = this.model;
    // Use either the regular game or the riddle (Gáta Dagsins)
    const game = model.game || model.riddle;

    // Update the board scale (zoom)

    const scrollIntoView = (sq: string) => {
      // Scroll a square above and to the left of the placed tile into view,
      // with a smooth concurrent zoom and pan animation,
      // taking clamping into account to ensure that the board always fills
      // the viewport.
      const boardParent = document.getElementById("board-parent");
      const board = boardParent?.children[0] as HTMLElement;
      if (!board || !boardParent) return;

      const offset = 3;
      const vec = toVector(sq);
      const row = Math.max(0, vec.row - offset);
      const col = Math.max(0, vec.col - offset);
      const c = coord(row, col);

      // board.style.transform = `translate(0, 0) scale(1.0)`;
      const el = document.getElementById("sq_" + c);
      const elRect = el?.getBoundingClientRect();
      const boardRect = boardParent.getBoundingClientRect();

      if (elRect && boardRect) {
        // Get the dimensions of the scrollable area
        const viewportWidth = boardParent.clientWidth;
        const viewportHeight = boardParent.clientHeight;
        // The offsetWidth/Height include borders, so we use those
        const scaledBoardWidth = board.offsetWidth * ZOOM_FACTOR;
        const scaledBoardHeight = board.offsetHeight * ZOOM_FACTOR;

        // Calculate maximum scroll positions (board edges)
        const maxScrollLeft = scaledBoardWidth - viewportWidth;
        const maxScrollTop = scaledBoardHeight - viewportHeight;

        // Calculate desired scroll position to put the target into
        // the top left corner of the viewport, or as close as possible
        let targetScrollLeft = Math.min(elRect.left - boardRect.left, maxScrollLeft);
        let targetScrollTop = Math.min(elRect.top - boardRect.top, maxScrollTop);

        // Cancel any pending zoom animation
        if (this.zoomAnimationTimeout !== null) {
          clearTimeout(this.zoomAnimationTimeout);
          this.zoomAnimationTimeout = null;
        }

        // Now animate both translate (for pan) and scale (for zoom) concurrently
        // Note: transforms are applied right to left
        board.style.transition = 'transform 0.3s ease-in-out';
        const translateValue = `${-targetScrollLeft}px, ${-targetScrollTop}px`;
        const scaleValue = `${ZOOM_FACTOR}`;
        board.style.transform = `translate(${translateValue}) scale(${scaleValue})`;

        // Use setTimeout as primary mechanism (transitionend is unreliable in Firefox/Safari)
        // 350ms = 300ms animation + 50ms buffer
        this.zoomAnimationTimeout = setTimeout(() => {
          this.zoomAnimationTimeout = null;
          // Reset the transform (remove translate, keep scale)
          board.style.transition = 'none';
          board.style.transform = `translate(0px, 0px) scale(${ZOOM_FACTOR})`;
          // Wait for the browser to process the style changes before setting scroll position
          // This is needed for Firefox and Safari to properly update the scroll position
          requestAnimationFrame(() => {
            boardParent.scrollTo(targetScrollLeft, targetScrollTop);
          });
        }, 350);
      }
    }

    if (!game || model.state?.uiFullscreen || game.moveInProgress) {
      // No game or we're in full screen mode: always 100% scale
      // Also, as soon as a move is being processed by the server, we zoom out
      this.boardScale = 1.0; // Needs to be done before setTimeout() call
      setTimeout(() => this.resetScale());
      return;
    }
    const tp = game.tilesPlaced();
    const numTiles = tp.length;
    if (numTiles === 1 && this.boardScale === 1.0) {
      // Laying down first tile: zoom in & position
      this.boardScale = ZOOM_FACTOR;
      setTimeout(() => scrollIntoView(tp[0]));
    }
    else if (numTiles === 0 && this.boardScale > 1.0) {
      // Removing only remaining tile: zoom out
      this.boardScale = 1.0; // Needs to be done before setTimeout() call
      setTimeout(() => this.resetScale());
    }
  }

  showUserInfo(userid: string, nick: string, fullname: string) {
    // Show a user info dialog
    this.pushDialog("userinfo", { userid: userid, nick: nick, fullname: fullname });
  }

  showFriendPromo() {
    // Show a friendship promotion
    this.pushDialog("friend", { });
  }

  showAcceptDialog(oppId: string, oppNick: string, challengeKey: string) {
    this.pushDialog("accept", { oppId, oppNick, challengeKey });
  }

  setSelectedTab(sel: string): boolean {
    // Set the currently selected tab; return true if it was actually changed
    if (this.selectedTab === sel)
      return false;
    this.selectedTab = sel;
    return true;
  };

  // Globally available view functions

  // A control that rigs up a tabbed view of raw HTML

  vwTabsFromHtml(
    html: string, id: string, tabNumber: number, createFunc: (vnode: TabVnode) => void
  ): VnodeChildren {
    // The function assumes that 'this' is the current view object
    if (!html)
      return "";
    return m("div.help-tabs",
      {
        oncreate: (vnode) => { makeTabs(this, id, createFunc, true, vnode); },
        oninit: (vnode) => { vnode.state.selected = tabNumber || 1; },
        /* onupdate: updateSelection */
      },
      m.trust(html)
    );
  }

  // Help screen

  vwHelp(tabNumber: number, faqNumber: number): Vnode {

    const model = this.model;

    function wireQuestions(vnode: TabVnode) {
      // Clicking on a question brings the corresponding answer into view
      // This is achieved by wiring up all contained a[href="#faq-*"] links

      function showAnswer(ev: Event, href: string) {
        // this points to the vnode
        vnode.state.selected = 1; // FAQ tab
        vnode.dom.querySelector(href)?.scrollIntoView();
        ev.preventDefault();
      }

      const anchors = vnode.dom.querySelectorAll("a");
      for (const anchor of anchors) {
        const href = anchor.getAttribute("href");
        if (href && href.slice(0, 5) == "#faq-")
          // This is a direct link to a question: wire it up
          anchor.onclick = (ev) => { showAnswer(ev, href); };
      }
      if (faqNumber !== undefined && !isNaN(faqNumber)) {
        // Go to the FAQ tab and scroll the requested question into view
        selectTab(vnode, 1);
        vnode.state.selected = 1; // FAQ tab
        vnode.dom.querySelector("#faq-" + faqNumber.toString())?.scrollIntoView();
      }
    }

    // Output literal HTML obtained from rawhelp.html on the server
    return m.fragment({ key: "help" }, [
      m(LeftLogo),
      m(UserId, { view: this }),
      this.vwTabsFromHtml(model.helpHTML || "", "tabs", tabNumber, wireQuestions),
    ]);
  }

  // User preferences screen

  vwUserPrefs(): VnodeChildren {
    const model = this.model;
    if (model.user === null && !model.userLoadError)
      model.loadUser(true); // Activate spinner while loading
    if (!model.user)
      // Nothing to edit (the spinner should be showing in this case)
      return m.fragment({ key: "userprefs-empty" }, []);
    return m(UserPrefsDialog, { key: "userprefs", view: this });
  }

  vwWait(args: {
    oppId: string;
    oppNick: string;
    oppName: string;
    duration: number;
    challengeKey: string;
  }): VnodeChildren {
    return m(WaitDialog, {
      key: "wait-" + args.challengeKey,
      view: this,
      oppId: args.oppId,
      oppNick: args.oppNick,
      oppName: args.oppName,
      duration: args.duration,
      challengeKey: args.challengeKey,
    });
  }

  vwAccept(args: { oppId: string; oppNick: string; challengeKey: string; }): VnodeChildren {
    return m(AcceptDialog, {
      key: "accept-" + args.challengeKey,
      view: this,
      oppId: args.oppId,
      oppNick: args.oppNick,
      challengeKey: args.challengeKey,
    });
  }

  vwDialogs(): VnodeChildren {
    // Show prompt dialogs below game board, if any
    return m(GamePromptDialogs, { view: this });
  }

} // class View
